import React, { useEffect, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import useAuth from "../../hooks/useAuth";
import useFetch from "../../hooks/useFetch";
import Pagination from "../../components/Table/pagination";
import { useFollowupNotification } from "../../context/FollowupNotificationContext";
import { useSIP } from "../../context/SIPContext";

// PDF DOWNLOAD //
import jsPDF from "jspdf";
import "jspdf-autotable";

// EXCEL DOWNLOAD //
import * as XLSX from "xlsx";
import { BarChart3 } from "lucide-react";

import { ReactComponent as Pdf } from "../../assets/icons/pdf-file-svgrepo-com.svg";
import { ReactComponent as Excel } from "../../assets/icons/excel-svgrepo-com.svg";

// feedback_labels / feedback_questions come back as JSONField data from the
// backend (arrays or objects), not plain strings - render defensively so an
// unexpected shape shows something readable instead of crashing the table.
const renderJsonField = (value) => {
  if (value === null || value === undefined || value === "") return "-";

  if (Array.isArray(value)) {
    if (value.length === 0) return "-";
    return value
      .map((item) =>
        typeof item === "object" && item !== null
          ? Object.values(item).join(": ")
          : String(item)
      )
      .join(", ");
  }

  if (typeof value === "object") {
    const entries = Object.entries(value);
    if (entries.length === 0) return "-";
    return entries.map(([k, v]) => `${k}: ${v}`).join(", ");
  }

  return String(value);
};

const FollowUpReport = (props) => {
  const { auth } = useAuth();

  const fetchData = useFetch();
  const { hasWebRtc } = useFollowupNotification();
  const { makeCall } = useSIP();

  const Initial_pagination = {
    next: "",
    previous: "",
    count: "",
    total_pages: "",
    current_page: 1,
    page_size: 5,
  };

  const [FollowUpList, setFollowUpList] = useState([]);
  const [pagination, setPagination] = useState(Initial_pagination);
  const [loading, setLoading] = useState(false);
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState("");
  const [agentSearch, setAgentSearch] = useState("");
  const [showAgentOptions, setShowAgentOptions] = useState(false);

  const PaginationControl = (type, number) => {
    if (type == "page") {
      if (number <= pagination.total_pages) {
        fetchCampainCustomers(number);
      } else {
        fetchCampainCustomers(1);
      }
    } else if (type == "last") {
      fetchCampainCustomers(pagination.total_pages);
    } else if (type == "prev") {
      if (pagination.current_page > 1) {
        fetchCampainCustomers(pagination.current_page - 1);
      }
    } else if (type == "next") {
      if (pagination.current_page < pagination.total_pages) {
        fetchCampainCustomers(pagination.current_page + 1);
      }
    } else {
      fetchCampainCustomers(1);
    }
  };

  const fetchAgents = async () => {
    try {
      const response = await fetchData(
        "get",
        {},
        `${process.env.REACT_APP_API_URL}/contactcentre/agents-dropdown/`
      );
      setAgents(response);
    } catch (err) {
      console.error("Error fetching agents:", err);
    }
  };

  const fetchCampainCustomers = async (pagenumber) => {
    let postData = {
      user_id: auth?.user_details?.user,
      user_type: props.user_type,
      userType : auth.user_details.type,
      page: pagenumber,
      input_text: searchText,
      from_date: fromDate || "",
      to_date: toDate || "",
      version: "v2",
      user_filter:selectedAgent,
    };

    let config = {
      headers: { "Content-Type": "application/json" },
    };

    setLoading(true);

    try {
      const res = await axios.get(
        process.env.REACT_APP_API_URL +
          "/contactcentre/FollowUpDetailedReportContactcenter/",
        {
          ...config,
          params: postData,
        }
      );

      setFollowUpList(res.data.results || []);

      setPagination((prevState) => ({
        ...prevState,
        total_pages: res.data?.total_pages || 0,
        previous: res.data?.previous || null,
        next: res.data?.next || null,
        current_page: res.data?.current_page || 1,
        count: res.data?.count || 0,
        page_size: res.data?.page_size || 5,
      }));
    } catch (err) {
      console.error("Failed to fetch follow-up report:", err);
      Swal.fire({
        icon: "error",
        title: "Failed to load report",
        text:
          err?.response?.data?.message ||
          "Something went wrong while fetching the follow-up report. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchAllFollowUpData = async () => {
    const params = {
      user_id: auth?.user_details?.user,
      user_type: props.user_type,
      userType: auth?.user_details?.type,
      input_text: searchText,
      from_date: fromDate || "",
      to_date: toDate || "",
      download: true,
      version: "v2",
      user_filter: selectedAgent || "",
    };

    try {
      const res = await axios.get(
        process.env.REACT_APP_API_URL +
          "/contactcentre/FollowUpDetailedReportContactcenter/",
        {
          headers: { "Content-Type": "application/json" },
          params: params,
        }
      );

      return Array.isArray(res.data) ? res.data : res.data.results || [];
    } catch (error) {
      console.error("Failed to fetch all follow-up data:", error);

      Swal.fire({
        icon: "error",
        title: "Download failed",
        text: "Unable to fetch all follow-up records.",
      });

      return [];
    }
  };

  useEffect(() => {
    fetchAgents();
    fetchCampainCustomers(1);
  }, []);

  const columnCount = hasWebRtc ? 9 : 8;

  const ExpandableComment = ({ text, limit = 15 }) => {
    const [expanded, setExpanded] = useState(false);

    if (!text) return null;

    const isLong = text.length > limit;
    const displayText = expanded || !isLong ? text : `${text.slice(0, limit)}...`;

    return (
      <span>
        {displayText}
        {isLong && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-blue-500 ml-1 text-sm font-medium hover:underline"
          >
            {expanded ? "Show less" : "Read more"}
          </button>
        )}
      </span>
    );
  };

  const [searchText, setSearchText] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const FetchData = () => {
      fetchCampainCustomers(1);
  };

  // -------   PDF & EXCEL DOWNLOAD (added, same pattern as CampaignReport)  -------- //

  const downloadpdfData = (dataset) => {
    try {
      const doc = new jsPDF();
      doc.text("Follow Up Report", 10, 10);

      const tableData = dataset.flatMap((obj, index) => {
        return [
          [index < 9 ? `0${index + 1}` : `${index + 1}`, 'Customer', `${obj.customer_name || ""} (${obj.customer_number || ""})`],
          ['', 'Agent', `${obj.agent_name || ""} (${obj.agent_number || ""})`],
          ['', 'Campaign', obj.campaign_name],
          ['', 'FollowUp Date', obj.follow_up_date],
          ['', 'Comment', obj.comment],
          ['', 'Feedback Labels', renderJsonField(obj.feedback_labels)],
          ['', 'Feedback Questions', renderJsonField(obj.feedback_questions)],
          ['', '', ''], // Empty row for spacing between entries
        ];
      });

      doc.autoTable({
        head: [['S.No', 'Head', 'Data']],
        body: tableData,
        startY: 20,
        styles: {
          cellPadding: 1,
          fontSize: 10,
        },
        columnStyles: {
          0: { cellWidth: 20 },
          1: { cellWidth: 50 },
          2: { cellWidth: 110 },
        },
      });

      doc.save("FollowUpReport.pdf");
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const downloadxcelData = (dataset) => {
    const excelData = dataset.map((obj) => ({
      "Customer Name": obj.customer_name,
      "Customer Number": obj.customer_number,
      "Agent": obj.agent_name,
      "Agent Number": obj.agent_number,
      "Campaign Name": obj.campaign_name,
      "FollowUp Date": obj.follow_up_date,
      "Comment": obj.comment,
      "Feedback Labels": renderJsonField(obj.feedback_labels),
      "Feedback Questions": renderJsonField(obj.feedback_questions),
    }));

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(excelData);
    XLSX.utils.book_append_sheet(wb, ws, "Follow Up Report");
    const blobData = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const excelBlob = new Blob([blobData], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = window.URL.createObjectURL(excelBlob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "followup_report.xlsx";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const downloadSheet = async (type) => {
    try {
      const allData = await fetchAllFollowUpData();

      if (!allData.length) {
        Swal.fire({
          icon: "info",
          title: "No data",
          text: "No records available to download.",
        });
        return;
      }

      if (type === "PDF") {
        downloadpdfData(allData);
      } else {
        downloadxcelData(allData);
      }
    } catch (error) {
      console.log(error);
    }
  };

  // -------   END PDF & EXCEL DOWNLOAD  -------- //

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-blue-500 flex items-center justify-center">
          <BarChart3 className="w-5 h-5 text-white" />
        </div>
        <h1 className="font-bold text-xl text-gray-900">Follow Up Report</h1>
      </div>

      <div className="flex flex-col">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-4">
          <div>
            <label htmlFor="followup-search" className="block text-sm font-semibold text-gray-700 mb-1.5">
              Search for items
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <svg
                  className="w-5 h-5 text-gray-500"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <input
                id="followup-search"
                type="text"
                className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full pl-10 p-2.5"
                placeholder="Search for items"
                onChange={(e) => setSearchText(e.target.value)}
              />
            </div>
          </div>

          {/* Agent Filter */}
          <div className="relative w-full">
            <label htmlFor="followup-search" className="block text-sm font-semibold text-gray-700 mb-1.5">
              Agent Filter
            </label>
            <div className="relative">
              <input
                type="text"
                value={agentSearch}
                onFocus={() => setShowAgentOptions(true)}
                onBlur={() => setTimeout(() => setShowAgentOptions(false), 150)}
                onChange={(event) => {
                  setAgentSearch(event.target.value);
                  setSelectedAgent("");
                  setShowAgentOptions(true);
                }}
                placeholder="Search agent"
                className="h-10 w-full rounded-md border border-gray-300 bg-white px-3 pr-8 text-sm"
                role="combobox"
                aria-expanded={showAgentOptions}
                aria-controls="agent-options"
                aria-autocomplete="list"
              />

              {agentSearch && (
                <button
                  type="button"
                  onClick={() => {
                    setSelectedAgent("");
                    setAgentSearch("");
                    setShowAgentOptions(false);
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500"
                  aria-label="Clear agent filter"
                >
                  ×
                </button>
              )}

              {showAgentOptions && (
                <div
                  id="agent-options"
                  className="absolute left-0 top-full z-10 mt-1 max-h-48 w-full overflow-y-auto rounded-lg border border-gray-300 bg-white shadow-lg"
                  role="listbox"
                >
                  <div
                    className="cursor-pointer px-3 py-2 text-sm hover:bg-gray-100"
                    onMouseDown={() => {
                      setSelectedAgent("");
                      setAgentSearch("");
                      setShowAgentOptions(false);
                    }}
                    role="option"
                    aria-selected={!selectedAgent}
                  >
                    All Agents
                  </div>

                  {agents
                    .filter((agent) =>
                      (agent.profile_name || "")
                        .toLowerCase()
                        .includes(agentSearch.toLowerCase())
                    )
                    .map((agent) => (
                      <div
                        key={agent.user_profile}
                        className="cursor-pointer px-3 py-2 text-sm hover:bg-gray-100"
                        onMouseDown={() => {
                          setSelectedAgent(String(agent.user_profile));
                          setAgentSearch(agent.profile_name || "");
                          setShowAgentOptions(false);
                        }}
                        role="option"
                        aria-selected={selectedAgent === String(agent.user_profile)}
                      >
                        {agent.profile_name}
                      </div>
                    ))}

                  {agents.filter((agent) =>
                    (agent.profile_name || "")
                      .toLowerCase()
                      .includes(agentSearch.toLowerCase())
                  ).length === 0 && (
                    <div className="px-3 py-2 text-sm text-gray-500">
                      No agents found
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">From</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <svg
                  className="w-4 h-4 text-gray-500"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path d="M20 4a2 2 0 0 0-2-2h-2V1a1 1 0 0 0-2 0v1h-3V1a1 1 0 0 0-2 0v1H6V1a1 1 0 0 0-2 0v1H2a2 2 0 0 0-2 2v2h20V4ZM0 18a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V8H0v10Zm5-8h10a1 1 0 0 1 0 2H5a1 1 0 0 1 0-2Z" />
                </svg>
              </div>
              <input
                name="start"
                type="date"
                className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full pl-10 p-2.5"
                onChange={(e) => setFromDate(e.target.value)}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">To</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <svg
                  className="w-4 h-4 text-gray-500"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path d="M20 4a2 2 0 0 0-2-2h-2V1a1 1 0 0 0-2 0v1h-3V1a1 1 0 0 0-2 0v1H6V1a1 1 0 0 0-2 0v1H2a2 2 0 0 0-2 2v2h20V4ZM0 18a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V8H0v10Zm5-8h10a1 1 0 0 1 0 2H5a1 1 0 0 1 0-2Z" />
                </svg>
              </div>
              <input
                name="end"
                type="date"
                className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full pl-10 p-2.5"
                onChange={(e) => setToDate(e.target.value)}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-transparent mb-1.5 select-none">Submit</label>
            <button
              type="button"
              className="w-full bg-blue-500 hover:bg-blue-700 text-white font-bold p-2.5 rounded-lg"
              onClick={FetchData}
            >
              Submit
            </button>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 mb-6">
          <button
            type="button"
            className="flex items-center gap-2 bg-red-100 px-3 py-2 rounded-2xl cursor-pointer hover:bg-red-200 transition"
            onClick={() => downloadSheet("PDF")}
          >
            <Pdf className="w-5 h-5" />
            <span className="text-red-500 font-medium">PDF</span>
          </button>

          <button
            type="button"
            className="flex items-center gap-2 bg-green-100 px-3 py-2 rounded-2xl cursor-pointer hover:bg-green-200 transition"
            onClick={() => downloadSheet("EXCEL")}
          >
            <Excel className="w-5 h-5" />
            <span className="text-green-500 font-medium">Excel</span>
          </button>
        </div>
     

        <div className="px-7 pb-2 min-w-full">
          <div className="overflow-x-auto rounded-xl border border-gray-200">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 text-gray-500 text-[13px] uppercase tracking-wide font-karla">
                  <th className="text-start font-semibold px-4 py-3 whitespace-nowrap w-[1%]">Sl-No</th>
                  <th className="text-start font-semibold px-4 py-3 whitespace-nowrap">Customer</th>
                  <th className="text-start font-semibold px-4 py-3 whitespace-nowrap">Agent</th>
                  <th className="text-start font-semibold px-4 py-3 whitespace-nowrap">Campaign</th>
                  <th className="text-start font-semibold px-4 py-3 whitespace-nowrap">FollowUp Date</th>
                  <th className="text-start font-semibold px-4 py-3 whitespace-nowrap">Comment</th>
                  <th className="text-start font-semibold px-4 py-3 whitespace-nowrap">Feedback Labels</th>
                  <th className="text-start font-semibold px-4 py-3 whitespace-nowrap">Feedback Questions</th>
                  <th className="text-start font-semibold px-4 py-3 whitespace-nowrap">Status</th>
                  {hasWebRtc && (
                    <th className="text-center font-semibold px-4 py-3 whitespace-nowrap">Call</th>
                  )}
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  <tr className="text-[15px] font-karla text-gray-500">
                    <td className="py-8 text-center" colSpan={columnCount}>
                      Loading...
                    </td>
                  </tr>
                ) : FollowUpList.length === 0 ? (
                  <tr className="text-[15px] font-karla text-gray-500">
                    <td className="py-8 text-center" colSpan={columnCount}>
                      No records found.
                    </td>
                  </tr>
                ) : (
                  FollowUpList.map((obj, index) => (
                    <tr
                      key={obj.id ?? `${obj.customer_number}-${index}`}
                      className="text-[15px] font-karla text-gray-900 hover:bg-blue-50/40 transition-colors"
                    >
                      <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                        {index + 1 + (pagination?.current_page - 1) * pagination?.page_size}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="font-semibold">{obj.customer_name}</div>
                        <div className="text-sm text-gray-500">{obj.customer_number}</div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="font-semibold">{obj.agent_name}</div>
                        <div className="text-sm text-gray-500">{obj.agent_number}</div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="inline-block rounded-full bg-blue-50 text-blue-600 text-sm font-medium px-3 py-1">
                          {obj.campaign_name}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {obj.follow_up_date}
                      </td>
                      <td className="px-4 py-3 max-w-[220px]">
                        <ExpandableComment text={obj.comment} />
                      </td>
                      <td className="px-4 py-3 max-w-[220px]">
                        <ExpandableComment text={renderJsonField(obj.feedback_labels)} />
                      </td>
                      <td className="px-4 py-3 max-w-[220px]">
                        <ExpandableComment text={renderJsonField(obj.feedback_questions)} />
                      </td>
                      <td className="py-4 pl-[0.5rem] pr-[1.5rem]">
                        {obj.closed ? (
                          <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-600">
                            <span className="h-1.5 w-1.5 rounded-full bg-gray-400" />
                            Closed
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 rounded-full bg-[#6BC698]/15 px-3 py-1 text-xs font-semibold text-[#2f9e6b]">
                            <span className="h-1.5 w-1.5 rounded-full bg-[#6BC698]" />
                            Open
                          </span>
                        )}
                      </td>
                      {hasWebRtc && (
                        <td className="px-4 py-3 text-center">
                          <button
                            type="button"
                            title={`Call ${obj.customer_number}`}
                            onClick={() => makeCall(obj.customer_number)}
                            className="inline-flex items-center gap-1.5 rounded-lg bg-[#2F6FED] hover:brightness-110 text-white text-[12.5px] font-semibold px-3.5 py-[7px]"
                          >
                            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
                            </svg>
                            Call
                          </button>
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {FollowUpList.length !== 0 ? (
          <div className="p-8">
            <Pagination
              pagination_data={pagination}
              PaginationControl={PaginationControl}
            />
          </div>
        ): <></>
        }
      </div>
    </div>
  );
};

export default FollowUpReport;